(() => {
  const initializeAnnouncement = (sourceElement) => {
    const cleanups = [];
    const announcementElement = sourceElement.querySelector('.header__announcement');
    const announcementTextElement = announcementElement instanceof HTMLElement
      ? announcementElement.querySelector('.header__announcement-text')
      : null;
    const announcementStyleHost = announcementElement instanceof HTMLElement ? announcementElement : sourceElement;
    const announcementEmojiImages = announcementElement instanceof HTMLElement
      ? Array.from(announcementElement.querySelectorAll('.header__announcement-emoji img'))
      : [];
    const announcementTextDefault = announcementTextElement ? announcementTextElement.textContent || '' : '';

    const affiliateHeroPendingClass = 'sb-affiliate-hero-pending';
    const affiliateActiveClass = 'sb-affiliate-active';
    const affiliateNameToken = `${sourceElement.dataset.affiliateNameToken || ''}`;
    const freeShippingThresholdToken = `${sourceElement.dataset.freeShippingThresholdToken || ''}`;
    const affiliateAnnouncementTemplate = `${sourceElement.dataset.affiliateAnnouncementTemplate || ''}`;
    const seasonalAnnouncementTemplate = `${sourceElement.dataset.announcementTemplate || ''}`;
    const affiliateAnnouncementGradient = `${sourceElement.dataset.affiliateAnnouncementGradient || ''}`;
    const affiliateAnnouncementEmoji = `${sourceElement.dataset.affiliateAnnouncementEmoji || ''}`;
    const currencyCode = `${sourceElement.dataset.currency || ''}`.trim().toUpperCase();
    const freeShippingThresholdCents = Number.parseInt(`${sourceElement.dataset.freeShippingThresholdCents || ''}`, 10);
    const fallbackFreeShippingThresholdDisplay = `${sourceElement.dataset.freeShippingThresholdDisplay || ''}`;

    let announcementCountdownTimer = null;

    // Cookie parsing lives in window.SBAffiliateState (defined by the inline
    // head script in theme.liquid, guaranteed to run before this deferred
    // script).
    const getAffiliateAnnouncementState = () => window.SBAffiliateState?.read() || null;
    const clearAffiliateAnnouncementState = () => window.SBAffiliateState?.clear();

    const setAffiliateHeroPendingState = (state) => {
      const rootNode = document.documentElement;
      if (!(rootNode instanceof HTMLElement)) return;

      if (state) {
        // Deliberately does NOT add sb-affiliate-hero-pending: that class
        // hides seasonal hero cards, and this script runs after the
        // spotlight section's parse-time swap has already revealed them -
        // re-adding it here blanked the swapped hero until window load.
        // The head script stamps it pre-paint; the spotlight section owns
        // lifting it.
        rootNode.classList.add(affiliateActiveClass);
        window.SBAffiliateAnnouncementState = state;
        return;
      }

      rootNode.classList.remove(affiliateHeroPendingClass, affiliateActiveClass);
      if ('SBAffiliateAnnouncementState' in window) {
        delete window.SBAffiliateAnnouncementState;
      }
    };

    const replaceToken = (input, token, replacement) => {
      const source = `${input || ''}`;
      const target = `${token || ''}`;
      if (!target) return source;
      return source.split(target).join(`${replacement || ''}`);
    };

    const setAnnouncementText = (text) => {
      if (!announcementTextElement) return;
      const normalizedText = `${text || ''}`;
      const lines = normalizedText.split('\n');
      announcementTextElement.textContent = '';
      lines.forEach((line, index) => {
        if (index > 0) announcementTextElement.appendChild(document.createElement('br'));
        announcementTextElement.appendChild(document.createTextNode(line));
      });
    };

    const resolveFreeShippingThresholdDisplay = () => {
      if (!Number.isFinite(freeShippingThresholdCents) || freeShippingThresholdCents <= 0) {
        return fallbackFreeShippingThresholdDisplay;
      }

      const storefrontPricing = window.SBStorefrontPricing;
      if (storefrontPricing && typeof storefrontPricing.formatMoney === 'function') {
        const formatted = storefrontPricing.formatMoney(freeShippingThresholdCents, currencyCode, { context: 'storefront' });
        if (`${formatted || ''}`.trim() !== '') return formatted;
      }

      return fallbackFreeShippingThresholdDisplay;
    };

    const resolveAnnouncementTemplate = (templateText) => {
      const sourceText = `${templateText || ''}`;
      if (!sourceText) return '';
      const freeShippingThresholdDisplay = resolveFreeShippingThresholdDisplay();
      return replaceToken(sourceText, freeShippingThresholdToken, freeShippingThresholdDisplay);
    };

    const getSeasonalAnnouncementText = () => {
      const defaultRenderedText = `${announcementTextDefault || ''}`.trim();
      if (defaultRenderedText) {
        const currentThresholdDisplay = `${fallbackFreeShippingThresholdDisplay || ''}`.trim();
        const formattedThresholdDisplay = `${resolveFreeShippingThresholdDisplay() || ''}`.trim();
        if (
          currentThresholdDisplay
          && formattedThresholdDisplay
          && currentThresholdDisplay !== formattedThresholdDisplay
          && defaultRenderedText.includes(currentThresholdDisplay)
        ) {
          return defaultRenderedText.split(currentThresholdDisplay).join(formattedThresholdDisplay);
        }
        return defaultRenderedText;
      }

      const resolvedText = resolveAnnouncementTemplate(seasonalAnnouncementTemplate).trim();
      return resolvedText || announcementTextDefault;
    };

    const getAffiliateAnnouncementText = (state) => {
      if (!state) return getSeasonalAnnouncementText();
      const sourceText = affiliateAnnouncementTemplate.trim();
      if (!sourceText) return getSeasonalAnnouncementText();
      let announcementText = replaceToken(sourceText, '__SB_AFFILIATE_PERCENT__', state.displayPercentage ?? state.discountPercentage ?? sourceElement.dataset.affiliateDiscountDepth);
      announcementText = replaceToken(announcementText, affiliateNameToken, state.affiliateName);
      announcementText = resolveAnnouncementTemplate(announcementText);
      return announcementText.trim() || getSeasonalAnnouncementText();
    };

    const applyAffiliateAnnouncement = (state) => {
      if (!state || !announcementTextElement) return;

      if (affiliateAnnouncementGradient) {
        announcementStyleHost.style.setProperty('--sb-announcement-background', affiliateAnnouncementGradient);
      }
      announcementEmojiImages.forEach((imageNode) => {
        if (!affiliateAnnouncementEmoji) return;
        imageNode.setAttribute('src', affiliateAnnouncementEmoji);
      });
      setAnnouncementText(getAffiliateAnnouncementText(state));
    };

    const syncAffiliateAnnouncementState = () => {
      const state = getAffiliateAnnouncementState();
      if (!state) {
        clearAffiliateAnnouncementState();
        applyAffiliateAnnouncement(null);
        setAffiliateHeroPendingState(null);
        return null;
      }

      applyAffiliateAnnouncement(state);
      setAffiliateHeroPendingState(state);
      return state;
    };

    syncAffiliateAnnouncementState();

    const countdownRoot = announcementElement instanceof HTMLElement
      ? announcementElement.querySelector('[data-announcement-countdown]')
      : sourceElement.querySelector('[data-announcement-countdown]');

    if (countdownRoot) {
      const endDateValue = countdownRoot.getAttribute('data-sale-end-date') || '';
      const parseSaleEndTimestamp = (value) => {
        const normalizedValue = `${value || ''}`.trim();
        if (!normalizedValue) return NaN;

        const dateOnlyMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!dateOnlyMatch) {
          const fallbackTimestamp = Date.parse(normalizedValue);
          return Number.isFinite(fallbackTimestamp) ? fallbackTimestamp : NaN;
        }

        const year = Number.parseInt(dateOnlyMatch[1], 10);
        const monthIndex = Number.parseInt(dateOnlyMatch[2], 10) - 1;
        const day = Number.parseInt(dateOnlyMatch[3], 10);
        const parsedDate = new Date(Date.UTC(year, monthIndex, day));

        if (
          parsedDate.getUTCFullYear() !== year
          || parsedDate.getUTCMonth() !== monthIndex
          || parsedDate.getUTCDate() !== day
        ) {
          return NaN;
        }

        const endOfDayUtcGuess = Date.UTC(year, monthIndex, day, 23, 59, 59);
        if (!window.Intl || typeof Intl.DateTimeFormat !== 'function') {
          return Date.parse(`${normalizedValue}T23:59:59-08:00`);
        }

        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        });

        if (typeof formatter.formatToParts !== 'function') {
          return Date.parse(`${normalizedValue}T23:59:59-08:00`);
        }

        const getOffsetMs = (utcTimestamp) => {
          const parts = formatter.formatToParts(new Date(utcTimestamp));
          const timeParts = {};

          for (const part of parts) {
            if (part.type !== 'literal') timeParts[part.type] = Number.parseInt(part.value, 10);
          }

          const localTimeAsUtc = Date.UTC(
            timeParts.year,
            timeParts.month - 1,
            timeParts.day,
            timeParts.hour,
            timeParts.minute,
            timeParts.second
          );

          return localTimeAsUtc - utcTimestamp;
        };

        let resolvedTimestamp = endOfDayUtcGuess;
        for (let index = 0; index < 4; index += 1) {
          const nextTimestamp = endOfDayUtcGuess - getOffsetMs(resolvedTimestamp);
          if (Math.abs(nextTimestamp - resolvedTimestamp) < 1) break;
          resolvedTimestamp = nextTimestamp;
        }

        return resolvedTimestamp;
      };

      const endTimestamp = parseSaleEndTimestamp(endDateValue);
      const daysValue = countdownRoot.querySelector('[data-countdown-days]');
      const hoursValue = countdownRoot.querySelector('[data-countdown-hours]');
      const minsValue = countdownRoot.querySelector('[data-countdown-mins]');
      const secsValue = countdownRoot.querySelector('[data-countdown-secs]');

      if (!Number.isFinite(endTimestamp) || !daysValue || !hoursValue || !minsValue || !secsValue) {
        countdownRoot.setAttribute('hidden', '');
      } else {
        const formatCountdownValue = (value) => `${Math.min(99, Math.max(0, value))}`.padStart(2, '0');
        const countdownDisplayThresholdSeconds = 5 * 24 * 60 * 60;

        const updateCountdown = () => {
          const remainingSeconds = Math.max(0, Math.floor((endTimestamp - Date.now()) / 1000));
          const debugForcedVisible = document.documentElement.hasAttribute('data-sb-announcement-countdown-debug');
          const shouldShowCountdown = debugForcedVisible
            || (remainingSeconds > 0 && remainingSeconds <= countdownDisplayThresholdSeconds);
          const days = Math.floor(remainingSeconds / 86400);
          const hours = Math.floor((remainingSeconds % 86400) / 3600);
          const mins = Math.floor((remainingSeconds % 3600) / 60);
          const secs = remainingSeconds % 60;

          if (shouldShowCountdown) countdownRoot.removeAttribute('hidden');
          else countdownRoot.setAttribute('hidden', '');

          daysValue.textContent = formatCountdownValue(days);
          hoursValue.textContent = formatCountdownValue(hours);
          minsValue.textContent = formatCountdownValue(mins);
          secsValue.textContent = formatCountdownValue(secs);

          if (remainingSeconds <= 0 && announcementCountdownTimer != null) {
            window.clearInterval(announcementCountdownTimer);
            announcementCountdownTimer = null;
          }
        };

        updateCountdown();
        document.addEventListener('sb:announcement-countdown-debug', updateCountdown);
        cleanups.push(() => document.removeEventListener('sb:announcement-countdown-debug', updateCountdown));
        announcementCountdownTimer = window.setInterval(updateCountdown, 1000);
      }
    }

    cleanups.push(() => {
      if (announcementCountdownTimer != null) {
        window.clearInterval(announcementCountdownTimer);
        announcementCountdownTimer = null;
      }
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  };

  class AnnouncementBarComponent extends HTMLElement {
    connectedCallback() {
      if (!this.cleanupAnnouncement) {
        this.cleanupAnnouncement = initializeAnnouncement(this);
      }
    }

    disconnectedCallback() {
      this.cleanupAnnouncement?.();
      this.cleanupAnnouncement = null;
    }
  }

  if (!customElements.get('announcement-bar-component')) {
    customElements.define('announcement-bar-component', AnnouncementBarComponent);
  }
})();
